/**
 * Mid-rental consent request (§7.2).
 *
 * Mobile-critical (§7.8): the counterparty has to be able to say yes or no from
 * a phone, so the change is presented as a field-level before/after list rather
 * than a two-column diff, and the two controls are full width.
 */

import { useParams } from 'react-router-dom';
import { useApi, useMutation } from '../../hooks/useApi';
import type { Rental } from '../../api/types';
import { Card, Loading } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { EmptyState } from '../../components/EmptyState';
import { humanize, localDateTime } from '../../lib/format';
import { routes } from '../../api/routes';

export function RentalConsent() {
  const { rentalId = '' } = useParams();
  const rental = useApi<Rental>(routes.rentals.one(rentalId), { pollMs: 60_000 });
  const respond = useMutation<{ accept: boolean }, unknown>(routes.rentals.consent(rentalId));

  if (rental.initialLoading) return <Loading label="Loading the request" />;
  if (rental.problem) return <ErrorView problem={rental.problem} onRetry={rental.reload} />;

  const consent = rental.data?.pending_consent ?? null;

  if (!consent) {
    return (
      <Card title="Consent">
        <EmptyState
          heading="Nothing is waiting on you"
          body="Mid-rental changes to the character appear here for the other side to accept or decline."
        />
      </Card>
    );
  }

  return (
    <Card title="A change is proposed">
      <p className="muted">
        Requested {localDateTime(consent.requested_at)} · based on character v{consent.base_version}
      </p>

      <ul className="consent-changes">
        {consent.changes.map((c) => (
          <li key={c.field} className="consent-change">
            <h3>{humanize(c.field)}</h3>
            <p className="consent-before">
              <span className="consent-label">Now</span> {c.before || '(empty)'}
            </p>
            <p className="consent-after">
              <span className="consent-label">Proposed</span> {c.after || '(empty)'}
            </p>
          </li>
        ))}
      </ul>

      {respond.problem ? <InlineError problem={respond.problem} /> : null}

      <button
        type="button"
        className="btn btn-primary btn-block"
        disabled={respond.pending}
        onClick={async () => {
          if ((await respond.run({ accept: true })) !== null) rental.reload();
        }}
      >
        Accept this change
      </button>
      <button
        type="button"
        className="btn btn-block"
        disabled={respond.pending}
        onClick={async () => {
          if ((await respond.run({ accept: false })) !== null) rental.reload();
        }}
      >
        Decline
      </button>
      <p className="muted">
        Declining leaves the character exactly as it is. The rental continues either way.
      </p>
    </Card>
  );
}
